<template>
  <div
    class="main-menu"
    data-test-id="main-menu-container"
  >
    <MenuItem
      v-for="(item, index) in mainMenu"
      :key="index"
      :item="item"
      :class="[
        {'first-item': menuVisibleItemsCounter > 2 && index === 0},
        {'last-item': menuVisibleItemsCounter > 2 && index === mainMenu.length - 1}
      ]"
    />
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { vuexTypes } from '@vuex/types'
  import { i18n } from '@js/i18n'
  import config from '@conf'
  import { ACCOUNT_TYPES } from '@const'
  import MenuItem from './menuItem.vue'

  export default {
    name: 'main-menu',

    components: {
      MenuItem
    },

    data () {
      return {
        i18n,
        config,
        ACCOUNT_TYPES
      }
    },

    computed: {
      ...mapGetters([
        vuexTypes.accountTypeI,
        vuexTypes.accountState,
        vuexTypes.accountSignerName,
        vuexTypes.approverData,
        vuexTypes.accountBlocked
      ]),
      accountVerified () {
        return this.accountTypeI !== this.ACCOUNT_TYPES.notVerified &&
          this.accountState !== 'pending'
      },
      approvalLevel () {
        return this.approverData.approval_level
      },
      refundHistory () {
        return [1, 2].includes(this.approvalLevel)
      },
      cashbox () {
        const name = this.accountSignerName
        return name ? name.toLowerCase().startsWith('cashbox') : false
      },
      widrawalRequest () {
        const isVisisble = this.approvalLevel && !this.accountBlocked
        return isVisisble
      },
      systemAccount () {
        let name = this.accountSignerName
        if (name) {
          name = name.toLowerCase()
          return name.startsWith('cashbox') ||
            name.startsWith('operational') ||
            name.startsWith('commission') ||
            name.startsWith('kfy')
        }
        return false
      },
      mainMenu () {
        return [
          {
            icon: '/static/img/dashboard_home.svg',
            tooltipText: i18n.lbl_dashboard(),
            routerLink: '/dashboard',
            visibilityRules: this.accountVerified
          },
          {
            icon: '/static/img/transactions_icon.svg',
            tooltipText: i18n.lbl_transactions(),
            visibilityRules: this.accountVerified,
            submenu: [
              {
                icon: '/static/img/account-cash-icon.svg',
                label: i18n.lbl_cashbox(),
                routerLink: '/cashbox',
                visibilityRules: this.config.FEATURE_FLAGS.cashbox && this.cashbox
              },
              {
                icon: '/static/img/account-cash-icon.svg',
                label: i18n.lbl_withdrawal_req(),
                routerLink: '/withdrawal-req',
                visibilityRules: this.config.FEATURE_FLAGS.widrawalRequest && this.widrawalRequest
              },
              {
                icon: '/static/img/top_up_icon.svg',
                label: i18n.lbl_refill(),
                routerLink: '/topup-balance',
                visibilityRules: this.config.FEATURE_FLAGS.topupBalance
              },
              {
                icon: '/static/img/withdraw_icon.svg',
                label: i18n.lbl_withdraw(),
                routerLink: '/withdrawal',
                visibilityRules: this.config.FEATURE_FLAGS.withdrawal && this.approvalLevel === 1
              },
              {
                icon: '/static/img/send_icon.svg',
                label: i18n.lbl_send(),
                routerLink: '/transfers',
                visibilityRules: this.config.FEATURE_FLAGS.transfers
              },
              {
                icon: '/static/img/transaction_history_icon.svg',
                label: i18n.lbl_history(),
                routerLink: '/history',
                visibilityRules: this.config.FEATURE_FLAGS.history && this.accountVerified
              },
              {
                icon: '/static/img/transaction_history_icon.svg',
                label: i18n.lbl_refund_history(),
                routerLink: '/refund-history',
                visibilityRules: this.config.FEATURE_FLAGS.refundHistory && this.refundHistory
              },
              {
                icon: '/static/img/masstransfer_icon.svg',
                label: i18n.lbl_send_mass(),
                routerLink: '/mass-transfers',
                visibilityRules: this.config.FEATURE_FLAGS.massTransfers
              }
            ]
          },
          {
            icon: '/static/img/projects_icon.svg',
            tooltipText: i18n.lbl_projects(),
            visibilityRules: this.config.FEATURE_FLAGS.projects && !this.systemAccount && this.accountVerified,
            submenu: [
              {
                icon: '/static/img/project_create_icon.svg',
                label: i18n.lbl_create_sale(),
                routerLink: '/sale-creation',
                visibilityRules: this.config.FEATURE_FLAGS.saleCreation && this.accountTypeI === this.ACCOUNT_TYPES.syndicate
              },
              {
                icon: '/static/img/my_project_icon.svg',
                label: i18n.lbl_my_funds(),
                routerLink: '/my-sales',
                visibilityRules: this.config.FEATURE_FLAGS.sales && this.accountTypeI === this.ACCOUNT_TYPES.syndicate
              },
              {
                icon: '/static/img/explore_project_icon.svg',
                label: i18n.lbl_explore_sales(),
                routerLink: '/sales',
                visibilityRules: this.config.FEATURE_FLAGS.sales
              },
              {
                icon: '/static/img/investment_icon.svg',
                label: i18n.lbl_investment_portfolio(),
                routerLink: '/portfolio',
                visibilityRules: this.config.FEATURE_FLAGS.portfolio && !this.systemAccount
              },
              {
                icon: '/static/img/requests_icon.svg',
                label: i18n.lbl_requests(),
                routerLink: '/requests',
                visibilityRules: this.config.FEATURE_FLAGS.requests && this.accountTypeI === this.ACCOUNT_TYPES.syndicate && !this.systemAccount
              },
              {
                icon: 'toll',
                label: i18n.lbl_explore_tokens(),
                routerLink: '/tokens',
                visibilityRules: this.config.FEATURE_FLAGS.tokens
              }
            ]
          },
          {
            icon: 'toll',
            tooltipText: i18n.lbl_explore_tokens(),
            routerLink: '/tokens',
            visibilityRules: this.config.FEATURE_FLAGS.tokens &&
              this.config.FEATURE_FLAGS.tokensItemInMenu &&
              this.systemAccount
          },
          {
            icon: '/static/img/fees_icon.svg',
            tooltipText: i18n.lbl_fee(),
            routerLink: '/fee',
            visibilityRules: this.config.FEATURE_FLAGS.myFees && this.accountVerified
          },
          {
            icon: '/static/img/limits_icon.svg',
            tooltipText: i18n.lbl_limits(),
            routerLink: '/limits',
            visibilityRules: this.config.FEATURE_FLAGS.limits && (this.accountTypeI === this.ACCOUNT_TYPES.syndicate || this.accountTypeI === this.ACCOUNT_TYPES.general)
          },
          {
            icon: '/static/img/trade_icon.svg',
            tooltipText: i18n.lbl_trade(),
            routerLink: '/trade',
            visibilityRules: this.config.FEATURE_FLAGS.trade && this.accountVerified
          },
          {
            icon: 'verified_user',
            tooltipText: i18n.lbl_verification(),
            routerLink: '/verification',
            visibilityRules: this.config.FEATURE_FLAGS.verification && (this.accountState === 'nil' || this.accountState === 'rejected')
          },
          {
            icon: 'autorenew',
            tooltipText: i18n.lbl_exchange(),
            routerLink: '/exchange',
            visibilityRules: this.config.FEATURE_FLAGS.trade && this.accountVerified
          },
          {
            icon: 'contact_mail',
            tooltipText: i18n.lbl_contracts(),
            routerLink: '/contracts',
            visibilityRules: this.config.FEATURE_FLAGS.contracts && !this.systemAccount && this.accountVerified
          },
          {
            icon: '/static/img/settings_icon.svg',
            tooltipText: i18n.lbl_settings(),
            routerLink: '/settings',
            visibilityRules: this.config.FEATURE_FLAGS.settings
          }
        ]
      },
      menuVisibleItemsCounter () {
        return this.mainMenu.filter(item => item.visibilityRules).length
      }
    },

    methods: { }
  }
</script>

<style scoped lang="scss">
  @import "~scss/variables";
  @import "~scss/mixins";

  .main-menu {
    border-radius: 999px;
    padding: 10px;
    background-color: $color-white;
    box-shadow: 4px 4px 4px 0 rgba(134, 134, 134, 0.2);
    display: flex;
    flex-direction: column;
    gap: 10px;

    .first-item {
      margin-bottom: 30px;
      position: relative;

      &:after {
        content: '';
        position: absolute;
        bottom: -20px;
        left: calc(50% - 18px);
        width: 85%;
        height: 1px;
        background-color: $grey;
      }
    }

    .last-item {
      margin-top: 30px;
      position: relative;

      &:after {
        content: '';
        position: absolute;
        top: -20px;
        left: calc(50% - 18px);
        width: 85%;
        height: 1px;
        background-color: $grey;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/vue/navigation/mainMenu/MainMenu.vue